import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XIcon, CheckCircleIcon, EyeIcon, ClipboardCheckIcon, MessageSquareIcon, TrendingUpIcon, AlertCircleIcon, LightbulbIcon, PlayCircleIcon, BookOpenIcon, AwardIcon, ArrowRightIcon, ArrowLeftIcon, HomeIcon, PhoneIcon, CalendarIcon, FileTextIcon, UserCheckIcon, MapPinIcon, ClockIcon, StarIcon } from 'lucide-react';
type QuestStep = {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  gradient: string;
  content: {
    tips: string[];
    warnings?: string[];
    checklist?: string[];
  };
};
export function ViewingsTutorial() {
  const navigate = useNavigate();
  const [currentQuest, setCurrentQuest] = useState(0);
  const [completedQuests, setCompletedQuests] = useState<string[]>([]);
  const quests: QuestStep[] = [{
    id: 'intro',
    title: 'Bun venit în Ghidul Vizionărilor',
    description: 'Învață cum să conduci vizionări profesionale și să închei tranzacții de succes',
    icon: EyeIcon,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-600',
    content: {
      tips: ['Vizionările sunt momentul cheie în care transformi un lead într-un client', 'O vizionare bine pregătită crește șansele de închidere cu 70%', 'Sistemul nostru te ghidează pas cu pas prin întreg procesul', 'Vei învăța tehnici profesionale folosite de top agenți']
    }
  }, {
    id: 'before',
    title: 'Înainte de Vizionare',
    description: 'Pregătirea este cheia succesului - ce trebuie să faci înainte',
    icon: ClipboardCheckIcon,
    color: 'emerald',
    gradient: 'from-emerald-500 to-green-600',
    content: {
      tips: ['Verifică disponibilitatea proprietății cu proprietarul cu 24h înainte', 'Confirmă vizionarea cu clientul cu 2-3 ore înainte (SMS + apel)', 'Studiază dosarul proprietății: suprafață, an construcție, utilități', 'Pregătește răspunsuri la întrebări frecvente (chirie, garanție, cheltuieli)', 'Verifică că ai toate documentele necesare (CI, contract mandat)'],
      warnings: ['NU programa mai mult de 3 vizionări pe zi - calitatea > cantitate', 'NU merge la vizionare fără să confirmi cu ambele părți'],
      checklist: ['Contract mandat semnat cu proprietarul', 'Chei proprietate / acces asigurat', 'Telefon încărcat + aplicație CRM', 'Caiet notițe pentru feedback', 'Aspect profesional (îmbrăcăminte business casual)']
    }
  }, {
    id: 'during',
    title: 'În Timpul Vizionării',
    description: 'Tehnici profesionale pentru o prezentare de impact',
    icon: HomeIcon,
    color: 'purple',
    gradient: 'from-purple-500 to-pink-600',
    content: {
      tips: ['Ajunge cu 10 minute înainte pentru a verifica starea proprietății', 'Deschide ferestrele pentru aerisire și aprinde luminile', 'Salută clientul profesional și prezintă-te (nume + agenție)', 'Lasă clientul să exploreze liber, nu-l urmări pas cu pas', 'Evidențiază punctele forte: lumină naturală, spațiu, locație', 'Răspunde onest la întrebări - transparența creează încredere', 'Observă limbajul non-verbal: entuziasmul sau rezervele clientului'],
      warnings: ['NU critica alte proprietăți sau agenții', 'NU pune presiune - lasă clientul să decidă în ritmul lui', 'NU minți despre defecte sau probleme - vor ieși la iveală'],
      checklist: ['Prezintă fiecare cameră și utilitatea ei', 'Arată sistemul de încălzire și contorizare', 'Explică cheltuielile lunare estimate', 'Discută despre vecinătate și facilități (transport, magazine)', 'Întreabă despre impresii și preocupări']
    }
  }, {
    id: 'after',
    title: 'După Vizionare',
    description: 'Indexarea corectă și follow-up profesional',
    icon: MessageSquareIcon,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    content: {
      tips: ['Indexează vizionarea IMEDIAT în sistem (în mașină, înainte să pleci)', 'Notează feedback-ul clientului: ce i-a plăcut, ce nu, preocupări', 'Stabilește următorul pas: așteaptă răspuns, programează semnare, sau rematching', 'Trimite mesaj de mulțumire clientului în 1-2 ore', 'Informează proprietarul despre rezultatul vizionării', 'Dacă clientul e interesat, trimite detalii suplimentare (poze, plan)'],
      warnings: ['NU lăsa vizionarea neindexată - pierzi track-ul și momentum-ul', 'NU uita să urmărești clientul - 80% din vânzări vin după follow-up'],
      checklist: ['Status vizionare actualizat în CRM', 'Feedback detaliat notat', 'Următorul pas stabilit (răspuns, semnare, rematching)', 'Client contactat pentru follow-up', 'Proprietar informat despre rezultat']
    }
  }, {
    id: 'system',
    title: 'Cum Funcționează Sistemul',
    description: 'Înțelege flow-ul și statusurile vizionărilor',
    icon: TrendingUpIcon,
    color: 'indigo',
    gradient: 'from-indigo-500 to-blue-600',
    content: {
      tips: ['📅 PROGRAMATĂ: Vizionare nouă, confirmă cu ambele părți', '🔄 REPROGRAMATĂ: Vizionare mutată, verifică noul interval', '⏳ AȘTEPT RĂSPUNS: Client se gândește, urmărește în 2-3 zile', '✍️ SEMNARE PROGRAMATĂ: Client dorește contract, pregătește documentele', '📄 SEMNAT - FACTURARE: Contract semnat, emite factura', '✅ COMISION ÎNCASAT: Tranzacție finalizată cu succes', '❌ ANULATĂ: Vizionare anulată, notează motivul', '🚨 NECESITĂ FEEDBACK: Vizionare trecută, indexează rezultatul URGENT'],
      warnings: ['Vizionările cu "Necesită Feedback" sunt PRIORITATE - indexează imediat', 'Sistemul te alertează automat când o vizionare necesită atenție']
    }
  }, {
    id: 'best-practices',
    title: 'Best Practices & Sfaturi Pro',
    description: 'Tehnici avansate de la top performeri',
    icon: AwardIcon,
    color: 'rose',
    gradient: 'from-rose-500 to-pink-600',
    content: {
      tips: ['🎯 Calificarea: Asigură-te că clientul e serios înainte de vizionare', '📸 Fotografii: Trimite poze proprietății înainte pentru pre-calificare', '⏰ Timing: Programează vizionări când lumina naturală e optimă (10-16)', '🗣️ Comunicare: Vorbește 30% din timp, ascultă 70%', '💡 Storytelling: Creează o poveste despre proprietate și cartier', '🤝 Rapport: Construiește relație personală, nu doar tranzacțională', '📊 Statistici: Menționează date despre zonă (prețuri, cerere, dezvoltare)', '🎁 Valoare adăugată: Oferă informații utile (școli, transport, magazine)'],
      checklist: ['Creează urgență naturală (alți clienți interesați)', 'Folosește tehnica "assumptive close" (presupune că vor închiria)', 'Pregătește răspunsuri la obiecții comune', 'Ai backup properties pentru rematching rapid', 'Urmărește metrici: rata de conversie, timp mediu până la închidere']
    }
  }];
  const currentQuestData = quests[currentQuest];
  const handleCompleteQuest = () => {
    if (!completedQuests.includes(currentQuestData.id)) {
      setCompletedQuests([...completedQuests, currentQuestData.id]);
    }
    if (currentQuest < quests.length - 1) {
      setCurrentQuest(currentQuest + 1);
    }
  };
  const handlePrevious = () => {
    if (currentQuest > 0) {
      setCurrentQuest(currentQuest - 1);
    }
  };
  const progress = completedQuests.length / quests.length * 100;
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-id="element-11360">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-id="element-11361">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-11362">
          <div className="flex items-center justify-between" data-id="element-11363">
            <div className="flex items-center space-x-4" data-id="element-11364">
              <button onClick={() => navigate('/rental/viewings')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-11365">
                <XIcon className="w-6 h-6 text-slate-600" data-id="element-11366" />
              </button>
              <div data-id="element-11367">
                <h1 className="text-2xl font-bold text-[#132C53]" data-id="element-11368">
                  Tutorial Vizionări
                </h1>
                <p className="text-sm text-slate-600" data-id="element-11369">
                  Quest {currentQuest + 1} din {quests.length}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-11370">
              <div className="text-right" data-id="element-11371">
                <p className="text-xs text-slate-500" data-id="element-11372">Progres</p>
                <p className="text-lg font-bold text-blue-600" data-id="element-11373">
                  {completedQuests.length}/{quests.length}
                </p>
              </div>
              <div className="w-16 h-16 relative" data-id="element-11374">
                <svg className="w-16 h-16 transform -rotate-90" data-id="element-11375">
                  <circle cx="32" cy="32" r="28" stroke="#e2e8f0" strokeWidth="6" fill="none" data-id="element-11376" />
                  <circle cx="32" cy="32" r="28" stroke="#3b82f6" strokeWidth="6" fill="none" strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`} strokeLinecap="round" className="transition-all duration-500" data-id="element-11377" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-11378">
                  <span className="text-sm font-bold text-blue-600" data-id="element-11379">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quest Progress Bar */}
      <div className="bg-white border-b border-slate-200" data-id="element-11380">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-11381">
          <div className="flex items-center space-x-2" data-id="element-11382">
            {quests.map((quest, index) => <Fragment key={quest.id} data-id="element-11383">
                <button onClick={() => setCurrentQuest(index)} className={`flex-1 h-2 rounded-full transition-all ${completedQuests.includes(quest.id) ? 'bg-gradient-to-r from-emerald-500 to-green-600' : index === currentQuest ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-slate-200'}`} data-id="element-11384" />
              </Fragment>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-11385">
        <AnimatePresence mode="wait" data-id="element-11386">
          <motion.div key={currentQuestData.id} initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -50
        }} transition={{
          duration: 0.3
        }} data-id="element-11387">
            {/* Quest Header */}
            <div className="text-center mb-12" data-id="element-11388">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br ${currentQuestData.gradient} rounded-3xl flex items-center justify-center shadow-2xl`} data-id="element-11389">
                <currentQuestData.icon className="w-12 h-12 text-white" data-id="element-11390" />
              </motion.div>
              <h2 className="text-4xl font-bold text-[#132C53] mb-4" data-id="element-11391">
                {currentQuestData.title}
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-11392">
                {currentQuestData.description}
              </p>
            </div>

            {/* Content Cards */}
            <div className="space-y-6" data-id="element-11393">
              {/* Tips */}
              {currentQuestData.content.tips && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200" data-id="element-11394">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-11395">
                    <div className={`w-10 h-10 bg-gradient-to-br ${currentQuestData.gradient} rounded-xl flex items-center justify-center`} data-id="element-11396">
                      <LightbulbIcon className="w-5 h-5 text-white" data-id="element-11397" />
                    </div>
                    <h3 className="text-xl font-bold text-[#132C53]" data-id="element-11398">
                      Sfaturi Esențiale
                    </h3>
                  </div>
                  <div className="space-y-4" data-id="element-11399">
                    {currentQuestData.content.tips.map((tip, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.2 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-11400">
                        <CheckCircleIcon className={`w-5 h-5 text-${currentQuestData.color}-600 flex-shrink-0 mt-0.5`} data-id="element-11401" />
                        <p className="text-slate-700 leading-relaxed" data-id="element-11402">{tip}</p>
                      </motion.div>)}
                  </div>
                </motion.div>}

              {/* Warnings */}
              {currentQuestData.content.warnings && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-8 shadow-lg border-2 border-red-200" data-id="element-11403">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-11404">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center" data-id="element-11405">
                      <AlertCircleIcon className="w-5 h-5 text-white" data-id="element-11406" />
                    </div>
                    <h3 className="text-xl font-bold text-red-900" data-id="element-11407">
                      Atenție - Greșeli Comune
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-11408">
                    {currentQuestData.content.warnings.map((warning, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.4 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-white rounded-xl" data-id="element-11409">
                        <XIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-11410" />
                        <p className="text-red-900 font-medium leading-relaxed" data-id="element-11411">
                          {warning}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}

              {/* Checklist */}
              {currentQuestData.content.checklist && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 shadow-lg border-2 border-emerald-200" data-id="element-11412">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-11413">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center" data-id="element-11414">
                      <ClipboardCheckIcon className="w-5 h-5 text-white" data-id="element-11415" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-900" data-id="element-11416">
                      Checklist Acțiuni
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-11417">
                    {currentQuestData.content.checklist.map((item, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.6 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow cursor-pointer" data-id="element-11418">
                        <div className="w-5 h-5 border-2 border-emerald-500 rounded flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-11419">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-11420" />
                        </div>
                        <p className="text-emerald-900 font-medium leading-relaxed" data-id="element-11421">
                          {item}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12" data-id="element-11422">
              <button onClick={handlePrevious} disabled={currentQuest === 0} className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all ${currentQuest === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-700 hover:bg-slate-50 shadow-md hover:shadow-lg'}`} data-id="element-11423">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-11424" />
                <span data-id="element-11425">Înapoi</span>
              </button>

              {currentQuest === quests.length - 1 ? <button onClick={() => {
              handleCompleteQuest();
              navigate('/inchiriere/vizionari');
            }} className="flex items-center space-x-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-xl hover:scale-105 transition-all" data-id="element-11426">
                  <AwardIcon className="w-5 h-5" data-id="element-11427" />
                  <span data-id="element-11428">Finalizează Tutorial</span>
                </button> : <button onClick={handleCompleteQuest} className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r ${currentQuestData.gradient} text-white hover:shadow-xl hover:scale-105 transition-all`} data-id="element-11429">
                  <span data-id="element-11430">Următorul Quest</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-11431" />
                </button>}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}